import { type ReactNode, useCallback, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { clsx } from "clsx";
import { ArrowRightLeft, Bell, BellOff, Briefcase, Gavel, Pencil, Trash2 } from "lucide-react";
import { Spinner } from "@/components/ui/Spinner";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useAuthStore } from "@/store/auth";
import {
  useBadgeMuteMutation, useConnectionAttention, useConnectionByProject, useReportSchedule,
} from "../hooks";
import { type BadgeColor, StatTile, SubTabs, Tabs, deadlineLabel, deadlineTone } from "../components/ui";
import { fmtDate, fmtMoney } from "../utils";
import { EditProjectDialog } from "../components/EditProjectDialog";
import { DeleteConnectionDialog } from "../components/DeleteConnectionDialog";
import { HandOverDialog } from "@/features/contractor_led/components/HandOverDialog";
import { ControlTab as LedControlTab } from "@/features/contractor_led/components/ControlTab";
import { FilesTab as LedFilesTab } from "@/features/contractor_led/components/FilesTab";
import { LedJournalPanel, LedMapPanel, LedProjectHeader, ledTabItems, type LedTabKey } from "@/features/contractor_led/components/LedSections";
import { MemosTab as LedMemosTab } from "@/features/contractor_led/components/MemosTab";
import { ParcelDrawer as LedParcelDrawer } from "@/features/contractor_led/components/ParcelDrawer";
import { ParcelsTable as LedParcelsTable } from "@/features/contractor_led/components/ParcelsTable";
import { RegisterTab as LedRegisterTab } from "@/features/contractor_led/components/RegisterTab";
import { ReportsTab as LedReportsTab } from "@/features/contractor_led/components/ReportsTab";
import { TilesTab as LedTilesTab } from "@/features/contractor_led/components/TilesTab";
import { LedPreviewProvider, useLedJournal, useLedParcels, useLedStatus, useLedSummary } from "@/features/contractor_led/hooks";
import type { LedParcel } from "@/features/contractor_led/types";
import { OperatorBadge } from "../components/OperatorBadge";
import { TopicStatusControl, effectiveStatusTone } from "../components/TopicStatusControl";
import { MapTab } from "../components/MapTab";
import { DrawingTab } from "../components/DrawingTab";
import { ParcelsTab } from "../components/ParcelsTab";
import { KwTab } from "../components/KwTab";
import { CorrespondenceTab } from "../components/CorrespondenceTab";
import { EopMailTab } from "../components/EopMailTab";
import { useEopMessages } from "@/features/eop_mail/api/mailbox";
import { ApplicationsTab } from "../components/wnioski/ApplicationsTab";
import { MilestonesTab } from "../components/MilestonesTab";
import { FilesTab } from "../components/FilesTab";
import { MetadataTab } from "../components/MetadataTab";
import { ProgressTimelineTab } from "../components/ProgressTimelineTab";
import { KpiTab } from "../components/KpiTab";
import { ReportsTab } from "../components/ReportsTab";
import { WpwMasoweTab } from "../components/WpwMasoweTab";
import { WodyTab } from "../components/WodyTab";
import { TechnicalProjectTab } from "../components/TechnicalProjectTab";
import {
  BarChart3,
  Bot,
  BookOpen,
  Droplets,
  FileSignature,
  FileText,
  Files,
  Flag,
  FolderOpen,
  HardHat,
  History,
  LandPlot,
  ListChecks,
  Mail,
  Map as MapIcon,
  PenTool,
  Settings2,
  Target,
} from "lucide-react";
import { KosztorysTab } from "../components/KosztorysTab";
import { ContractorsTab } from "../components/ContractorsTab";
import { AdvisorWorkspace } from "../advisor/AdvisorWorkspace";
import { ProjectTodoTab } from "../advisor/TodoView";
import { DocumentationTab } from "../components/DocumentationTab";

// „Opieka" (karta specyfiki klienta) mieszka jako sekcja w Metadanych, a spotkania
// projektu — w module Spotkania; obie zakładki zniknęły z paska (2026-09).
type TabKey =
  | "advisor" | "todo" | "milestones" | "docs" | "map" | "dxf" | "parcels" | "kw" | "wpw" | "wody"
  | "correspondence" | "eopmail" | "wnioski" | "kpi" | "reports" | "contractors" | "timeline" | "metadata" | "files"
  // Projekt prowadzony przez podwykonawcę (apps.contractor_led): zakładki nadzoru z widoku
  // firmy, z przedrostkiem — „Działki" i „Raporty" to już karty warsztatu. Mapa i Pliki
  // NIE mają bliźniaków: w trybie prowadzenia pokazują wersję z widoku firmy.
  | "led_parcels" | "led_register" | "led_tiles" | "led_memos" | "led_reports" | "led_journal" | "led_control";

/** Zakładka widoku firmy → klucz w warsztacie (mapa i pliki zachowują swoje klucze). */
const LED_TAB_KEY: Record<LedTabKey, TabKey> = {
  map: "map", files: "files", parcels: "led_parcels", register: "led_register", tiles: "led_tiles",
  memos: "led_memos", reports: "led_reports", journal: "led_journal", control: "led_control",
};
const LED_GROUP_TONE = "#1f3f8f";
/** Karty warsztatu schowane na projekcie prowadzonym przez firmę (decyzja usera 2026-10-08):
 *  ich odpowiedniki są w zakładkach nadzoru (Działki i strony, Rejestr spraw, Dziennik) albo
 *  nie dotyczą projektu prowadzonego z zewnątrz. Mapa zostaje mapą warsztatu. */
const LED_HIDDEN_TABS: ReadonlySet<TabKey> = new Set<TabKey>([
  "advisor", "todo", "dxf", "parcels", "kw", "correspondence", "eopmail", "timeline", "kpi", "metadata",
]);

/** Karta działki z widoku firmy otwierana z linku (?parcel=…), np. z przekierowania karty firmy. */
function searchParamsInitialParcel(): string | null {
  if (typeof window === "undefined") return null;
  return new URLSearchParams(window.location.search).get("parcel");
}

// Sub-tabs of the merged "Dokumentacja" tab (dokumentacja + projekt tech. + kosztorys).
type DocsSubKey = "docs" | "technical" | "kosztorys";

const DOCS_SUBTABS: { key: DocsSubKey; label: string }[] = [
  { key: "docs", label: "Dokumentacja" },
  { key: "technical", label: "Projekt tech." },
  { key: "kosztorys", label: "Kosztorys" },
];

// Nadgrupy paska zakładek. Dwadzieścia zakładek w jednym rzędzie to ściana tekstu,
// w której nic nie wiadomo, gdzie szukać — klastry dzielą je wg tego, CZYM się w danej
// chwili zajmujesz: prowadzeniem tematu, terenem, papierami do urzędu/operatora,
// dokumentacją, ludźmi. Kolor jest identyfikatorem grupy (szyna, podpis i podkreślenie
// aktywnej zakładki), nie dekoracją. Zakładka spoza każdej grupy NIE ginie — ląduje
// w bezimiennym klastrze na końcu (patrz Tabs).
const TAB_GROUPS: { label: string; tone: string; keys: TabKey[] }[] = [
  {
    label: "Prowadzenie",
    tone: "#dc1f1f",
    keys: ["advisor", "todo", "milestones", "kpi"],
  },
  {
    label: "Teren i trasa",
    tone: "#0f7b46",
    keys: ["map", "dxf", "parcels", "kw", "wpw", "wody"],
  },
  {
    label: "Urzędy i operator",
    tone: "#1f6fdc",
    keys: ["wnioski", "correspondence", "eopmail", "reports"],
  },
  {
    label: "Dokumenty",
    tone: "#7b2fbe",
    keys: ["docs", "files", "metadata"],
  },
  {
    label: "Zespół",
    tone: "#8a6a1f",
    keys: ["contractors", "timeline"],
  },
];

// Ikona jest drugim (po kolorze grupy) punktem zaczepienia dla oka — przy dwudziestu
// pozycjach sam tekst wersalikami zlewa się w ścianę. Rozmiar 12 px trzyma wysokość
// kafla; etykieta zostaje, bo sama ikona nie odróżni „Wniosków" od „Spraw".
const TABS: { key: TabKey; label: string; icon: React.ReactNode }[] = [
  { key: "advisor", label: "Doradca AI", icon: <Bot size={12} /> },
  { key: "todo", label: "TODO", icon: <ListChecks size={12} /> },
  { key: "milestones", label: "Kamienie", icon: <Flag size={12} /> },
  { key: "map", label: "Mapa", icon: <MapIcon size={12} /> },
  { key: "dxf", label: "Rysunek", icon: <PenTool size={12} /> },
  { key: "parcels", label: "Działki", icon: <LandPlot size={12} /> },
  { key: "kw", label: "KW", icon: <BookOpen size={12} /> },
  { key: "wpw", label: "WPW", icon: <FileSignature size={12} /> },
  { key: "wody", label: "Wody Polskie", icon: <Droplets size={12} /> },
  { key: "docs", label: "Dokumentacja", icon: <FolderOpen size={12} /> },
  { key: "correspondence", label: "Sprawy", icon: <Gavel size={12} /> },
  { key: "eopmail", label: "Poczta EOP", icon: <Mail size={12} /> },
  { key: "wnioski", label: "Wnioski", icon: <FileText size={12} /> },
  { key: "kpi", label: "KPI", icon: <Target size={12} /> },
  { key: "reports", label: "Raporty ZE", icon: <BarChart3 size={12} /> },
  { key: "contractors", label: "Podwykonawcy", icon: <HardHat size={12} /> },
  { key: "timeline", label: "Historia", icon: <History size={12} /> },
  { key: "files", label: "Pliki", icon: <Files size={12} /> },
  { key: "metadata", label: "Metadane", icon: <Settings2 size={12} /> },
];

// ── nagłówek: chipy statusów, pasek skrótów, komórki metadanych ──────────────
const HEADER_ACTION_CLS =
  "inline-flex items-center gap-1.5 px-3 font-mono text-[10px] font-medium uppercase tracking-wider " +
  "text-[var(--color-text-secondary)] hover:text-[var(--color-primary)] transition-colors";

/** Barwy chipa statusu — ta sama paleta co Badge (tło / ramka / tekst), ale nałożona
 *  na CAŁY chip z etykietą, żeby status czytał się kolorem z daleka. */
const CHIP_TONE: Record<BadgeColor, { frame: string; divider: string }> = {
  green: { frame: "border-green-200 bg-green-50 text-green-700", divider: "border-green-200" },
  yellow: { frame: "border-amber-200 bg-amber-50 text-amber-700", divider: "border-amber-200" },
  blue: { frame: "border-blue-200 bg-blue-50 text-blue-700", divider: "border-blue-200" },
  black: { frame: "border-gray-700 bg-gray-900 text-gray-100", divider: "border-gray-700" },
  red: { frame: "border-red-200 bg-red-50 text-red-700", divider: "border-red-200" },
  orange: { frame: "border-orange-200 bg-orange-50 text-orange-700", divider: "border-orange-200" },
  purple: { frame: "border-purple-200 bg-purple-50 text-purple-700", divider: "border-purple-200" },
  gray: {
    frame: "border-[var(--color-border)] bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)]",
    divider: "border-[var(--color-border)]",
  },
};

/** Chip „etykieta | wartość" w ramce stałej wysokości i w kolorze statusu, żeby nie
 *  ginął przy nazwie ani nie spadał do drugiego wiersza. */
function HeaderChip({
  label,
  tone,
  title,
  children,
}: {
  label: string;
  tone: BadgeColor;
  title?: string;
  children: ReactNode;
}) {
  const t = CHIP_TONE[tone];
  return (
    <div title={title} className={clsx("inline-flex h-[30px] items-stretch border", t.frame)}>
      <span className={clsx("section-label inline-flex items-center border-r px-2 text-current opacity-80", t.divider)}>
        {label}
      </span>
      <span className="inline-flex items-center px-2.5">{children}</span>
    </div>
  );
}

function HeaderMeta({ label, value, mono }: { label: string; value?: string | null; mono?: boolean }) {
  return (
    <div className="min-w-0 px-3.5 py-2">
      <dt className="section-label">{label}</dt>
      <dd
        className={clsx(
          "mt-0.5 truncate text-[13px] leading-snug",
          mono && "font-mono",
          value ? "font-semibold text-[var(--color-text-primary)]" : "text-[var(--color-text-muted)]",
        )}
        title={value || undefined}
      >
        {value || "—"}
      </dd>
    </div>
  );
}

/** Podgląd „jak podwykonawca" (projekt prowadzony przez firmę) musi opakowywać CAŁĄ stronę —
 *  hooki modułu prowadzenia czytają flagę z kontekstu, więc provider nie może siedzieć w tym
 *  samym komponencie co zapytania. */
export function PowerProjectWorkspacePage() {
  const [preview, setPreview] = useState(false);
  return (
    <LedPreviewProvider value={preview}>
      <WorkspaceBody preview={preview} onPreview={setPreview} />
    </LedPreviewProvider>
  );
}

function WorkspaceBody({ preview, onPreview }: { preview: boolean; onPreview: (v: boolean) => void }) {
  const { id: projectId } = useParams<{ id: string }>();
  const user = useAuthStore((s) => s.user);
  // Doradca AI ZE: CEO / dyrektor zawsze; wszyscy koordynatorzy DLC_01 (widzą graf
  // procesu); pozostałe role z grantem CEO „Doradca AI (ZE)" (flaga has_ze_advisor_access).
  // Mirror serwerowego CanAccessZEAdvisor — użytkownik bez dostępu do DLC_01 i tak nie
  // otworzy tej strony, więc rola „coordinator" wystarcza.
  const canSeeAdvisor =
    !!user &&
    (user.is_ceo ||
      user.role === "director" ||
      user.role === "coordinator" ||
      !!user.has_ze_advisor_access);

  // Poczta EOP — dostęp do modułu ZE to jeszcze nie prawo do czytania korespondencji
  // z operatorem. Odwzorowuje backendowe HasEopMailAccess; koordynator przedsięwzięcia
  // ENERGA ma je z automatu, więc gating idzie po `permission`, nie po samej fladze.
  const canSeeEopMail = !!user?.has_eop_mail_permission;
  const { data: connection, isLoading } = useConnectionByProject(projectId);
  // Projekt przekazany podwykonawcy (apps.contractor_led): nad zakładkami to, co widzi firma
  // (nagłówek, kafelki, tytuły do potwierdzenia), pod nimi zakładki nadzoru z widoku firmy
  // PLUS pełne karty warsztatu (Sprawy, Działki…); mapa i Pliki w wersji z widoku firmy.
  // Podgląd „jak podwykonawca" zawęża całość do zakładek firmy (decyzja właściciela, 2026-10-07).
  const { data: ledStatus } = useLedStatus(connection?.id);
  const leadershipId = ledStatus?.led ? ledStatus.leadership?.id : undefined;
  const ledSummaryQuery = useLedSummary(leadershipId);
  const ledParcelsQuery = useLedParcels(leadershipId);
  const [ledDays, setLedDays] = useState(7);
  const ledJournalQuery = useLedJournal(leadershipId, ledDays);
  const [openLedParcel, setOpenLedParcel] = useState<string | null>(searchParamsInitialParcel());
  const onSelectLedParcel = useCallback((p: LedParcel) => setOpenLedParcel(p.id), []);
  const ledSummary = ledSummaryQuery.data?.summary;
  const ledInternal = !!ledSummaryQuery.data?.internal;
  const ledMode = !!leadershipId && !!ledSummary;
  const canDecideLed = !!user && (user.is_ceo || (user.role === "director" && !!user.is_technical_director));
  const [handingOver, setHandingOver] = useState(false);
  // Licznik na zakładce POCZTA EOP: wątki z flagą „wymaga akcji" (czerwony),
  // a gdy ich nie ma — nieprzeczytane (niebieski). Ten sam klucz zapytania co
  // treść zakładki, więc TanStack Query dedupuje żądania.
  const eopThreads =
    useEopMessages(
      { project: connection?.project ?? "", groupByThread: true },
      { enabled: canSeeEopMail && !!connection?.project },
    ).data ?? [];
  const eopAction = eopThreads.filter((t) => t.thread_action).length;
  const eopUnread = eopThreads.filter((t) => t.thread_unread).length;
  // Edycja projektu (nazwa, EPSG, umowa, projektant): CEO / dyrektor techniczny zawsze,
  // koordynator tylko na swoim przedsięwzięciu — lustro backendu (IsTechnicalDirector
  // lub Project.coordinators w set_project_name).
  const canEditProject =
    !!user &&
    (user.is_ceo ||
      (user.role === "director" && user.is_technical_director) ||
      (!!connection && (connection.project_coordinators ?? []).includes(user.id)));
  // Status tematu (REALIZACJA / PÓŁKA / KOŃCZENIE / ZAMKNIĘTY): edycja wyłącznie
  // CEO / dyrektor techniczny (lustro bramki topic-status/); koordynator NIE.
  const canEditTopicStatus =
    !!user && (user.is_ceo || (user.role === "director" && user.is_technical_director));
  // Usunięcie (miękkie) projektu ZE: każdy dyrektor i CEO — lustro IsDirectorOrCEO na destroy.
  const canDeleteProject = !!user && (user.is_ceo || user.role === "director");
  // Skok z projektu do przetargu ZE, z którego powstał — dla kierownictwa (CEO /
  // dyrektorzy). Link istnieje też w Metadanych i na Mapie, ale dyrektor wchodzi tu
  // po ofertę i analizę przetargu, więc dostaje go wprost w nagłówku.
  const canJumpToTender = !!user && (user.is_ceo || user.role === "director");
  // Skok do przedsięwzięcia (panel Przedsięwzięcia). Pokazujemy tylko tym, którzy
  // faktycznie tę stronę otworzą — lustro guardów trasy /projects/:id
  // (TechnicalDirectorGuard + RoleGuard director/coordinator/accountant): dyrektor
  // NIEtechniczny jest z niej wyrzucany, więc nie dostaje przycisku donikąd.
  const canOpenEnterprise =
    !!user &&
    (user.is_ceo ||
      (user.role === "director" && user.is_technical_director) ||
      user.role === "coordinator" ||
      user.role === "accountant");
  const { data: attention } = useConnectionAttention(connection?.id);
  const muteMutation = useBadgeMuteMutation(connection?.id ?? "");
  // Kafelek „Raport ZE” w nagłówku: wejście do raportu o postępie prac (pismo / formatka
  // operatora) było schowane w głębi zakładki i koordynatorzy pisali formatkę ręcznie.
  const { data: reportSchedule } = useReportSchedule(connection?.id);
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();
  // Kwota przedsięwzięcia startuje zwinięta („•••") — odsłania ją dopiero klik, żeby
  // wartość kontraktu nie wisiała na ekranie przy każdym otwarciu projektu.
  const [valueRevealed, setValueRevealed] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  // Legacy deep links: ?tab=technical / ?tab=kosztorys now live as sub-tabs of "docs".
  const rawTab = searchParams.get("tab");
  // Legacy: „wpw-masowe" scalone z „wpw" (jedna zakładka z całym obiegiem WPW);
  // „care" (Opieka) to dziś sekcja w Metadanych, „meetings" zniknęło z paska.
  const tab: TabKey =
    rawTab === "technical" || rawTab === "kosztorys" ? "docs"
    : rawTab === "wpw-masowe" ? "wpw"
    : rawTab === "care" ? "metadata"
    : rawTab === "meetings" ? "milestones"
    : (rawTab as TabKey) || "milestones";
  const docsSub: DocsSubKey =
    rawTab === "technical" || rawTab === "kosztorys"
      ? rawTab
      : (searchParams.get("sub") as DocsSubKey) || "docs";

  // Karta przeglądarki: „ZE - <nazwa projektu>". Przy kilku otwartych projektach
  // ZE naraz sam prefiks „Projekt ZE" z trasy nie pozwalał ich rozróżnić.
  usePageTitle(connection ? `ZE - ${connection.project_name}` : undefined);

  if (isLoading)
    return (
      <div className="p-10 flex justify-center">
        <Spinner />
      </div>
    );

  if (!connection) {
    return (
      <div className="p-6 md:p-10 max-w-2xl mx-auto">
        <div className="card p-8 text-center">
          <p className="section-label mb-3">
            To przedsięwzięcie nie jest jeszcze projektem ZE
          </p>
          <Link
            to="/power-projects"
            className="font-mono text-sm font-bold uppercase tracking-wider text-[var(--color-primary)] hover:underline"
          >
            → Utwórz / zarządzaj projektami w portfelu
          </Link>
        </div>
      </div>
    );
  }

  const p = connection.progress;
  const at = attention?.tabs;
  const det = attention?.detail;
  // Parcel attention detail — feeds the header StatTile „Działki" (the tab itself
  // carries no x/y chip any more: the same fraction sits in the tile above).
  const parcelActionRequired = det?.parcels.action_required ?? 0;

  // a tab badge is "danger" (red) when it carries a hard problem, else "warn" (amber)
  const dangerTab: Partial<Record<TabKey, boolean>> = det
    ? {
        milestones: det.milestones.overdue > 0,
        wpw: det.wpw.problem > 0,
        correspondence: det.correspondence.kpa_overdue > 0,
        docs: det.technical.route_suspended,
        kpi: det.kpi.overdue > 0,
      }
    : {};
  const visibleTabs = TABS.filter(
    (t) =>
      (t.key !== "advisor" || canSeeAdvisor) &&
      (t.key !== "eopmail" || canSeeEopMail),
  );
  const tabsWithCounts = visibleTabs.map((t) => {
    if (t.key === "parcels") {
      // Postęp działek czyta się z kafelka w nagłówku — zakładka zostaje bez licznika.
      return { ...t, count: 0 };
    }
    if (t.key === "docs") {
      // Merged tab: attention rollup has no "docs" key — sum its sub-tabs.
      return {
        ...t,
        count: at ? (at.technical ?? 0) + (at.kosztorys ?? 0) : 0,
        countTone: dangerTab.docs ? ("danger" as const) : ("warn" as const),
      };
    }
    if (t.key === "eopmail") {
      return {
        ...t,
        count: eopAction || eopUnread,
        countTone: eopAction ? ("danger" as const) : ("blue" as const),
      };
    }
    return {
      ...t,
      count: at ? (at as Record<string, number>)[t.key] ?? 0 : 0,
      countTone: dangerTab[t.key] ? ("danger" as const) : ("warn" as const),
    };
  });
  // Zakładki nadzoru (z widoku firmy) — w trybie prowadzenia przed kartami warsztatu;
  // w podglądzie „jak podwykonawca" zostają TYLKO one (firma kart warsztatu nie ma).
  const ledTabs = ledMode && ledSummary
    ? ledTabItems(ledSummary.badges, ledJournalQuery.data?.recent, ledInternal && !preview).map((t) => ({
        ...t,
        key: LED_TAB_KEY[t.key],
        label: t.key === "parcels" ? "Działki i strony" : t.label,
      }))
    : [];
  const ledKeys = new Set(ledTabs.map((t) => t.key));
  const allTabs = ledMode
    ? (preview
        ? ledTabs
        : [...ledTabs, ...tabsWithCounts.filter((t) => !ledKeys.has(t.key) && !LED_HIDDEN_TABS.has(t.key))])
    : tabsWithCounts;
  const visibleKeys = new Set(allTabs.map((t) => t.key));
  const allGroups = ledMode
    ? [
        { label: "Prowadzenie przez firmę", tone: LED_GROUP_TONE, keys: ledTabs.map((t) => t.key) },
        ...TAB_GROUPS.map((g) => ({ ...g, keys: g.keys.filter((k) => visibleKeys.has(k)) })),
      ]
    : TAB_GROUPS;
  // Karta schowana w trybie prowadzenia (albo każda karta warsztatu w podglądzie) — spadamy na mapę.
  const activeTab: TabKey = ledMode && !visibleKeys.has(tab) ? "map" : tab;
  const docsSubtabsWithCounts = DOCS_SUBTABS.map((t) => ({
    ...t,
    count: at ? (at as Record<string, number>)[t.key] ?? 0 : 0,
    countTone:
      t.key === "technical" && det?.technical.route_suspended
        ? ("danger" as const)
        : ("warn" as const),
  }));

  // 5-level parcel summary tone, matching the worst per-parcel badge colour:
  //   czerwony  — any problem parcel
  //   żółty     — no problems but action_required parcels (need attention)
  //   zielony   — all parcels agreed
  //   niebieski — in progress (some agreed or other activity, no warnings)
  //   szary     — not started (nothing agreed yet, no warnings, no problems)
  // "All agreed" = every parcel that needs a title/zwrotka has one; also true when the
  // project has parcels but none of them require anything (all NOT_REQUIRED).
  const allRequiredAgreed =
    p.parcels_total > 0 &&
    (p.parcels_required > 0 ? p.parcels_ok_required === p.parcels_required : true);
  const parcelTone =
    p.parcels_problem > 0
      ? ("danger" as const)
      : parcelActionRequired > 0
        ? ("warn" as const)
        : allRequiredAgreed
          ? ("ok" as const)
          : p.parcels_ok_required > 0
            ? ("blue" as const)
            : ("default" as const);
  const parcelHint =
    p.parcels_problem > 0
      ? `⚠ ${p.parcels_problem} problem`
      : parcelActionRequired > 0
        ? `${parcelActionRequired} wymaga działania`
        : allRequiredAgreed
          ? "uzgodnione"
          : p.parcels_ok_required > 0
            ? "w toku"
            : "nie rozpoczęto";

  return (
    <div className="p-3 md:p-4 max-w-[1600px] mx-auto">
      {ledMode && ledSummary ? (
        // Projekt prowadzony przez firmę: nad zakładkami dokładnie to, co widzi firma
        // (nagłówek z przełącznikiem podglądu, pasek faktów, kafelki, tytuły do potwierdzenia).
        <LedProjectHeader
          summary={ledSummary}
          internal={ledInternal}
          preview={preview}
          onPreview={onPreview}
          isContractor={false}
          onOpenParcel={setOpenLedParcel}
          backTo="/power-projects"
          backLabel="Portfel projektów ZE"
        />
      ) : (
        <>
      {/* ── nagłówek projektu ──────────────────────────────────────────────
          Wiersz 1: operator + nazwa. Wiersz 2: dwa statusy tej samej wielkości
          (temat / umowa) po lewej, opcje i skróty w JEDNYM pasku po prawej.
          Wiersz 3: oddział / koordynator / projektant / umowa / EPSG jako
          komórki etykieta+wartość (zamiast jednego ciągu wersalikami).
          Wiersz 4: kafelki postępu. */}
      <div className="mb-5">
        <Link
          to="/power-projects"
          className="section-label inline-flex items-center gap-1 hover:text-[var(--color-primary)] transition-colors"
        >
          ← Portfel projektów ZE
        </Link>
        <div className="mt-1 flex items-start gap-3">
          {/* Operator (OSD) — od niego zależą szablony, wnioskodawca wypisu i opisy
              prac. Po LEWEJ od tytułu, stała szerokość, bez kurczenia: nazwy projektów
              ZE bywają trzywierszowe i zjadały znacznik. */}
          <OperatorBadge
            operator={connection.operator}
            operatorDisplay={connection.operator_display}
          />
          <div className="min-w-0 flex-1">
            <h1 className="text-xl md:text-2xl font-semibold leading-tight text-[var(--color-text-primary)]">
              {connection.project_name}
            </h1>
            <div className="h-[3px] w-12 bg-[var(--color-primary)] mt-2" />
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-stretch gap-2">
            {/* Jeden status: temat (realizacja / półka / kończenie / zamknięty), który los
                umowy WYPIERA — po wypowiedzeniu chip mówi „Umowa wypowiedziana" i nie ma menu
                do ręcznej zmiany (zmienia się przez dokumenty umowy na karcie przedsięwzięcia). */}
            <HeaderChip
              label="Status"
              tone={effectiveStatusTone(connection.topic_status, connection.project_contract_status)}
              title="Status tematu; wypowiedzenie umowy wypiera go i zmienia się tylko przez dokumenty umowy (dyrektor techniczny)"
            >
              <TopicStatusControl
                connectionId={connection.id}
                status={connection.topic_status}
                canEdit={canEditTopicStatus}
                contractStatus={connection.project_contract_status}
                contractTerminatedOn={connection.project_contract_terminated_on}
                projectId={connection.project}
                plain
              />
            </HeaderChip>
          </div>

          {(canOpenEnterprise ||
            (canJumpToTender && connection.source_tender) ||
            attention?.can_mute ||
            canEditProject ||
            canDeleteProject) && (
            <div className="card inline-flex h-[30px] items-stretch divide-x divide-[var(--color-border)]">
              {canOpenEnterprise && (
                <Link
                  to={`/projects/${connection.project}`}
                  title="Przejdź do przedsięwzięcia (panel Przedsięwzięcia)"
                  className={HEADER_ACTION_CLS}
                >
                  <Briefcase size={14} /> Przedsięwzięcie
                </Link>
              )}
              {canJumpToTender && connection.source_tender && (
                <Link
                  to={`/ze-tenders/${connection.source_tender}`}
                  title={
                    connection.source_tender_info?.reference_number
                      ? `Przejdź do przetargu ZE (${connection.source_tender_info.reference_number})`
                      : "Przejdź do przetargu ZE, z którego powstał projekt"
                  }
                  className={HEADER_ACTION_CLS}
                >
                  <Gavel size={14} /> Przetarg
                </Link>
              )}
              {/* Director who is NOT this project's manager can mute its badges (nav aggregate +
                  portfolio) to avoid spam; the project's own page still shows them. */}
              {attention?.can_mute && (
                <button
                  type="button"
                  onClick={() => muteMutation.mutate(!attention.is_muted)}
                  disabled={muteMutation.isPending}
                  title={
                    attention.is_muted
                      ? "Badge tego projektu są wyciszone w nawigacji — kliknij, aby włączyć"
                      : "Wycisz badge tego projektu w nawigacji (bez spamu dla nie-kierownika)"
                  }
                  className={HEADER_ACTION_CLS}
                >
                  {attention.is_muted ? <BellOff size={14} /> : <Bell size={14} />}
                  {attention.is_muted ? "Wyciszony" : "Wycisz"}
                </button>
              )}
              {canEditProject && (
                <button
                  type="button"
                  onClick={() => setEditing(true)}
                  title="Edytuj projekt (nazwa, EPSG, umowa, projektant)"
                  className={clsx(HEADER_ACTION_CLS, "text-[var(--color-primary)]")}
                >
                  <Pencil size={14} /> Edytuj
                </button>
              )}
              {canDeleteProject && (
                <button
                  type="button"
                  onClick={() => setDeleting(true)}
                  title="Usuń projekt ZE (dane zostają w systemie — administrator może go przywrócić)"
                  className={HEADER_ACTION_CLS}
                >
                  <Trash2 size={14} /> Usuń
                </button>
              )}
              {/* Przekazanie prowadzenia projektu podwykonawcy — tylko CEO / dyrektor techniczny
                  (lustro backendowego IsTechnicalDirector na hand-over/). */}
              {canEditTopicStatus && ledStatus && !ledStatus.led && (
                <button
                  type="button"
                  onClick={() => setHandingOver(true)}
                  title="Przekaż prowadzenie projektu podwykonawcy (tworzy zamówienie „Prowadzenie zlecenia”)"
                  className={HEADER_ACTION_CLS}
                >
                  <ArrowRightLeft size={14} /> Przekaż podwykonawcy
                </button>
              )}
            </div>
          )}
        </div>

        <dl className="card mt-3 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 divide-x divide-[var(--color-border-muted)]">
          <HeaderMeta
            label="Oddział"
            value={[connection.branch_name, connection.sub_branch_name].filter(Boolean).join(" / ")}
          />
          <HeaderMeta
            label="Koordynator"
            value={(connection.project_coordinator_names ?? []).join(", ")}
          />
          <HeaderMeta label="Projektant" value={connection.designer_name} />
          <HeaderMeta label="Umowa" value={connection.contract_number} mono />
          <HeaderMeta label="Układ (EPSG)" value={String(connection.srid)} mono />
        </dl>

        <div className="mt-3 flex flex-wrap items-stretch gap-2">
          <StatTile
            label="Działki"
            // uzgodnione / wykorzystywane (wszystkie) — działki zamówione w wypisach
            // „na zapas" nie mierzą postępu; wszystkie tylko w nawiasie, gdy się różnią.
            value={`${p.parcels_ok_used}/${p.parcels_used}${
              p.parcels_total !== p.parcels_used ? ` (${p.parcels_total})` : ""
            }`}
            tone={parcelTone}
            hint={parcelHint}
          />
          <StatTile
            label="Kamienie"
            value={`${p.milestones_done}/${p.milestones_total}`}
            tone={p.milestones_done === p.milestones_total && p.milestones_total > 0 ? "ok" : "default"}
            hint="ukończone"
          />
          <StatTile
            label="Sprawy"
            value={p.open_agreements}
            tone={p.open_agreements > 0 ? "warn" : "ok"}
            hint="otwarte"
          />
          <StatTile
            label="Start"
            value={fmtDate(connection.contract_signed_at)}
            hint="data zawarcia umowy"
          />
          {connection.contract_deadline && (
            <StatTile
              label="Termin"
              value={fmtDate(connection.contract_deadline)}
              tone={deadlineTone(connection.deadline_status)}
              hint={deadlineLabel(connection.deadline_status)}
            />
          )}
          <StatTile
            label="Raport ZE"
            value={
              reportSchedule?.next_slot
                ? fmtDate(reportSchedule.next_slot.due_date)
                : reportSchedule
                  ? "doraźny"
                  : "…"
            }
            tone={
              reportSchedule?.next_slot?.state === "overdue"
                ? "danger"
                : reportSchedule?.next_slot?.state === "due_soon"
                  ? "warn"
                  : "default"
            }
            hint={
              reportSchedule?.next_slot
                ? `raport nr ${reportSchedule.next_slot.sequence_no} → przygotuj`
                : "o postępie prac → przygotuj"
            }
            onClick={() => setSearchParams({ tab: "reports" })}
            title="Raport o postępie prac projektowych: pismo (e-mail) lub formatka operatora ENERGA (Załącznik nr 4 do OWU) — zakładka Raporty ZE"
          />
          {/* Wartość przedsięwzięcia — kafelek widzą wyłącznie osoby z prawem do kwot
              tego przedsięwzięcia (backend i tak nie przysyła kwoty pozostałym). */}
          {connection.can_view_project_value && (
            <StatTile
              label="Wartość"
              value={valueRevealed ? fmtMoney(connection.project_value) : "••• zł"}
              hint="przedsięwzięcia"
              onClick={() => setValueRevealed((v) => !v)}
              title={valueRevealed ? "Ukryj wartość" : "Pokaż wartość"}
            />
          )}
        </div>
      </div>

        </>
      )}

      <Tabs
        tabs={allTabs}
        groups={allGroups}
        // Opt-in per przeglądarkę: kto woli jeden pasek, ma go dalej domyślnie.
        // W podglądzie „jak podwykonawca" bez przełącznika — kilka kart nadzoru w jednej
        // grupie, grupowanie nic tam nie daje (zapisany wybór wraca po wyjściu z podglądu).
        groupingStorageKey={preview ? undefined : "elg5:ze-workspace-tab-groups"}
        sticky
        active={activeTab}
        onChange={(k) => setSearchParams({ tab: k })}
      />

      {/* ── zakładki nadzoru projektu prowadzonego przez firmę ── */}
      {ledMode && ledSummary && (
        <>
          {activeTab === "led_parcels" && (
            <LedParcelsTable parcels={ledParcelsQuery.data?.parcels ?? []} projectName={ledSummary.short_name || ledSummary.name} onOpen={setOpenLedParcel} />
          )}
          {activeTab === "led_register" && (
            <LedRegisterTab leadershipId={ledSummary.id} internal={ledInternal} parcels={ledParcelsQuery.data?.parcels ?? []} onOpenParcel={setOpenLedParcel} />
          )}
          {activeTab === "led_tiles" && <LedTilesTab leadershipId={ledSummary.id} internal={ledInternal} contractorName={ledSummary.contractor_name} />}
          {activeTab === "led_memos" && (
            <LedMemosTab leadershipId={ledSummary.id} internal={ledInternal} contractorName={ledSummary.contractor_name} parcels={ledParcelsQuery.data?.parcels ?? []} />
          )}
          {activeTab === "led_reports" && <LedReportsTab leadershipId={ledSummary.id} internal={ledInternal} />}
          {activeTab === "led_journal" && (
            <LedJournalPanel
              summary={ledSummary}
              internal={ledInternal}
              days={ledDays}
              onDaysChange={setLedDays}
              journal={ledJournalQuery.data}
              loading={ledJournalQuery.isLoading}
            />
          )}
          {activeTab === "led_control" && ledInternal && <LedControlTab summary={ledSummary} canDecide={canDecideLed} />}
          <LedParcelDrawer
            leadershipId={ledSummary.id}
            parcelId={openLedParcel}
            internal={ledInternal}
            contractorName={ledSummary.contractor_name}
            onClose={() => setOpenLedParcel(null)}
          />
        </>
      )}


      {activeTab === "advisor" && canSeeAdvisor && <AdvisorWorkspace connection={connection} />}
      {activeTab === "todo" && <ProjectTodoTab connectionId={connection.id} />}
      {activeTab === "docs" && (
        <>
          <SubTabs
            tabs={docsSubtabsWithCounts}
            active={docsSub}
            onChange={(k) => setSearchParams({ tab: "docs", sub: k })}
          />
          {docsSub === "docs" && <DocumentationTab connection={connection} />}
          {docsSub === "technical" && <TechnicalProjectTab connection={connection} />}
          {docsSub === "kosztorys" && <KosztorysTab connection={connection} />}
        </>
      )}
      {activeTab === "milestones" && <MilestonesTab connection={connection} />}
      {/* Mapa warsztatu także na projekcie prowadzonym przez firmę — w trybie `led`
          wgrywanie trasy jak u firmy (bez wersji), MDCP i mapa zasadnicza pod mapą.
          „Widok podwykonawcy" pokazuje za to DOKŁADNIE mapę firmy (LedMapPanel): bez
          obrysów i punktów MDCP, z punktami tylko gdy przekazane. */}
      {activeTab === "map" && ledMode && ledSummary && preview && (
        <LedMapPanel
          summary={ledSummary}
          parcelsData={ledParcelsQuery.data}
          parcelsLoading={ledParcelsQuery.isLoading}
          tutorialHref={`/power-projects/led/${ledSummary.id}/route-tutorial`}
          onSelectParcel={onSelectLedParcel}
        />
      )}
      {activeTab === "map" && !(ledMode && preview) && (
        <MapTab
          connection={connection}
          led={ledMode && ledSummary ? {
            leadershipId: ledSummary.id,
            hasRoute: !!ledSummary.route,
            dxf: ledSummary.route?.dxf ?? null,
            tutorialHref: `/power-projects/led/${ledSummary.id}/route-tutorial`,
          } : undefined}
        />
      )}
      {activeTab === "dxf" && <DrawingTab connection={connection} />}
      {activeTab === "parcels" && <ParcelsTab connection={connection} />}
      {activeTab === "kw" && <KwTab connection={connection} />}
      {activeTab === "wpw" && <WpwMasoweTab connection={connection} />}
      {activeTab === "wody" && <WodyTab connection={connection} />}
      {activeTab === "correspondence" && <CorrespondenceTab connection={connection} />}
      {activeTab === "eopmail" && canSeeEopMail && <EopMailTab connection={connection} />}
      {activeTab === "wnioski" && <ApplicationsTab connectionId={connection.id} />}
      {activeTab === "kpi" && <KpiTab connection={connection} />}
      {activeTab === "reports" && <ReportsTab connection={connection} />}
      {activeTab === "contractors" && <ContractorsTab connection={connection} />}
      {activeTab === "timeline" && <ProgressTimelineTab connection={connection} />}
      {activeTab === "files" && (ledMode && ledSummary ? <LedFilesTab leadershipId={ledSummary.id} /> : <FilesTab connection={connection} />)}
      {activeTab === "metadata" && <MetadataTab connection={connection} />}

      {editing && <EditProjectDialog connection={connection} onClose={() => setEditing(false)} />}
      {deleting && (
        <DeleteConnectionDialog
          connectionId={connection.id}
          projectName={connection.project_name}
          onClose={() => setDeleting(false)}
          onDeleted={() => navigate("/power-projects", { replace: true })}
        />
      )}
      {handingOver && (
        <HandOverDialog
          connectionId={connection.id}
          onClose={() => setHandingOver(false)}
          onDone={() => setHandingOver(false)}
        />
      )}
    </div>
  );
}
